// 重新定义需要的类型，避免循环导入
export enum TaskStatus {
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  BLOCKED = 'blocked',
  REVIEW = 'review',
}

export enum TaskPriority {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

// 任务清单类型枚举
export enum TaskListType {
  STATIC = 'static',
  DYNAMIC = 'dynamic',
}

// 任务实体
export interface Task {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  deletedAt?: string | null;
  isCompleted: boolean;
  title: string;
  content?: string;
  status: TaskStatus;
  priority: TaskPriority;
  startTime?: string;
  dueDate?: string;
  completedAt?: string;
  completionToken?: string;
  duration: number;
  progress: number;
  tags?: string[];
  taskListIds?: string[];
  taskListUuids?: string[];
  taskLists?: Array<string | { uuid?: string }>;
  color?: string;
  parentTaskId?: string;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
  tenantId: string;
  organizationId: string;
  creatorMemberUuid: string;
  assignee?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
  taskMembers?: Array<{
    memberUuid: string;
    roleCode: string;
    roleName?: string;
    isPrimary: boolean;
    name?: string;
    avatar?: string;
    position?: string;
  }>;
  creator?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
  attachments?: TaskAttachment[];
  subtasks?: Task[];
  watchers?: Array<{
    uuid: string;
    name: string;
    avatar?: string;
  }>;
  watcherMembers?: Array<{
    uuid: string;
    name: string;
    avatar?: string;
  }>;
  watchersCount?: number;
  isWatchedByMe?: boolean;
  dependencySummary?: {
    strongPredecessorUnfinishedCount?: number;
    weakPredecessorUnfinishedCount?: number;
    predecessorCount?: number;
    successorCount?: number;
  };
}

export enum TaskDependencyType {
  STRONG = 'strong',
  WEAK = 'weak',
}

export interface TaskDependencyEdge {
  uuid: string;
  predecessorTaskUuid: string;
  successorTaskUuid: string;
  dependencyType: TaskDependencyType;
  predecessorTask?: Pick<Task, 'uuid' | 'title' | 'status'>;
  successorTask?: Pick<Task, 'uuid' | 'title' | 'status'>;
}

export interface TaskDependencyGraph {
  currentTask: Pick<Task, 'uuid' | 'title' | 'status'>;
  predecessorTasks: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
  successorTasks: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
  directDependencies: TaskDependencyEdge[];
  upstreamEdges?: TaskDependencyEdge[];
  downstreamEdges?: TaskDependencyEdge[];
  cycleRisk?: boolean;
}

export interface TaskDependencyQueryData {
  relatedTasks: Task[];
  edges: TaskDependencyEdge[];
}

export interface SmartDimensionAnalysis {
  score?: number;
  reason?: string;
  suggestion?: string;
}

export interface TaskCompletionSmartAnalysisResult {
  overallScore?: number;
  summary?: string;
  dimensions?: {
    specific?: SmartDimensionAnalysis;
    measurable?: SmartDimensionAnalysis;
    achievable?: SmartDimensionAnalysis;
    relevant?: SmartDimensionAnalysis;
    timeBound?: SmartDimensionAnalysis;
  };
  completionAnalysis?: {
    status?: TaskStatus | string;
    progress?: number;
    isCompleted?: boolean;
    overdue?: boolean;
    riskLevel?: 'low' | 'medium' | 'high' | string;
    reason?: string;
    suggestion?: string;
  };
  dependencyContext?: {
    predecessorCount?: number;
    successorCount?: number;
    strongUnfinishedPredecessorCount?: number;
    weakUnfinishedPredecessorCount?: number;
    blockedByStrongDependency?: boolean;
    atRiskPredecessorCount?: number;
    atRiskSuccessorCount?: number;
    completedPredecessorCount?: number;
    completedSuccessorCount?: number;
    predecessorStatusSummary?: Record<string, number>;
    successorStatusSummary?: Record<string, number>;
    predecessorDetails?: Array<{
      uuid: string;
      title: string;
      status: TaskStatus | string;
      progress: number;
      dependencyType: TaskDependencyType | string;
      dueDate?: string;
      overdue: boolean;
      riskLevel: 'low' | 'medium' | 'high' | string;
    }>;
    successorDetails?: Array<{
      uuid: string;
      title: string;
      status: TaskStatus | string;
      progress: number;
      dependencyType: TaskDependencyType | string;
      dueDate?: string;
      overdue: boolean;
      riskLevel: 'low' | 'medium' | 'high' | string;
    }>;
    recommendations?: string[];
    insight?: string;
  };
  activityContext?: {
    totalRecentLogs?: number;
    latestActivityType?: string;
    latestActivityAt?: string;
    latestActivityDescription?: string;
    activityTypeSummary?: Record<string, number>;
    insight?: string;
  };
  rewriteSuggestion?: string;
  provider?: string;
  model?: string;
  rawText?: string;
}

export interface TaskStartability {
  canStart: boolean;
  strongUnfinished: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
  weakUnfinished: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
}

export interface TaskDeletePreview {
  task: Pick<Task, 'uuid' | 'title'>;
  subtasks: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
  predecessorTasks: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
  successorTasks: Array<Pick<Task, 'uuid' | 'title' | 'status'>>;
}

export interface TaskDeleteStrategy {
  subtaskAction?: 'unlink' | 'delete_all';
  dependencyAction?: 'none' | 'bridge_auto' | 'bridge_custom';
  bridgePairs?: Array<{
    predecessorTaskUuid: string;
    successorTaskUuid: string;
    dependencyType?: TaskDependencyType;
  }>;
}

export enum TaskQueryScope {
  RELATED = 'related',
  MY_TASKS = 'my-tasks',
  ASSIGNED = 'assigned',
  CREATED = 'created',
  ASSIGNED_OUT = 'assigned-out',
  WATCHED = 'watched',
  BOARDS = 'boards',
  TAGS = 'tags',
  TASKLISTS = 'tasklists',
}

export enum TaskSortBy {
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  TITLE = 'title',
  PRIORITY = 'priority',
  STATUS = 'status',
  DUE_DATE = 'dueDate',
  START_TIME = 'startTime',
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type TaskRelation = 'assigned' | 'created' | 'watched';

export interface TaskUnifiedQueryParams {
  scope: TaskQueryScope;
  /** related 范围内的关系并集；省略或空数组表示三类全部 */
  relations?: TaskRelation[];
  boardUuids?: string[];
  panelUuids?: string[];
  tagUuids?: string[];
  taskListUuids?: string[];
  status?: TaskStatus[];
  isCompleted?: boolean;
  isDeleted?: boolean;
  priority?: TaskPriority[];
  startTime?: number;
  endTime?: number;
  taskMemberUuid?: string;
  creatorMemberUuid?: string;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
  search?: string;
  includeDependencyGraph?: boolean;
  page?: number;
  limit?: number;
  sortBy?: TaskSortBy;
  sortOrder?: SortOrder;
}

export interface TaskUnifiedQueryResponse {
  success: boolean;
  data: {
    tasks: Task[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasPrev: boolean;
      hasNext: boolean;
    };
    filters: {
      applied: string[];
      count: number;
    };
    statistics: {
      completedCount: number;
      completionRate: number;
      highPriorityCount: number;
      pendingCount: number;
      totalItems: number;
    };
    dependencyGraph?: TaskDependencyQueryData;
  };
  message?: string;
  timestamp?: string;
}

// 通用任务查询参数（用于分页查询）
export interface TaskQueryParams {
  page?: number;
  limit?: number;
  status?: string | string[];
  priority?: string | string[];
  [key: string]: string | number | string[] | boolean | undefined;
}

// 任务看板面板配置
export interface TaskBoardPanel {
  id: string;
  taskListUuid: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  title: string;
  color?: string;
  icon?: string;
}

// 任务看板布局配置
export interface TaskBoardLayout {
  columns: number;
  rows: number;
  gap: number;
}

// 任务看板配置
export interface TaskBoardConfig {
  panels: TaskBoardPanel[];
  layout: TaskBoardLayout;
}

export type TaskBoardMemberRole = 'viewer' | 'editor' | 'admin';

export interface TaskBoardMemberAssignment {
  memberUuid: string;
  role: TaskBoardMemberRole;
}

export type TaskBoardCurrentUserRole = 'owner' | TaskBoardMemberRole;

// 任务看板实体
export interface TaskBoard {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  name: string;
  description?: string;
  type?: 'personal' | 'member' | 'list' | 'tag';
  typeUuid?: string; // 存储具体选择的UUID（成员UUID、清单UUID、标签UUID等）
  config?: TaskBoardConfig;
  isDefault: boolean;
  isPublic: boolean;
  memberAssignments: TaskBoardMemberAssignment[];
  currentUserRole: TaskBoardCurrentUserRole;
  sortWeight: number;
  creatorMemberUuid: string;
  tenantId: string;
  organizationId: string;
  color?: string;
  icon?: string;
  creator?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
}

// 创建任务看板DTO
export interface CreateTaskBoardDto {
  name: string;
  description?: string;
  type?: 'personal' | 'member' | 'list' | 'tag';
  typeUuid?: string; // 存储具体选择的UUID
  isDefault?: boolean;
  isPublic?: boolean;
  memberAssignments?: TaskBoardMemberAssignment[];
  sortWeight?: number;
  tenantId?: string;
  organizationId?: string;
}

// 更新任务看板DTO
export interface UpdateTaskBoardDto {
  name?: string;
  description?: string;
  type?: 'personal' | 'member' | 'list' | 'tag';
  typeUuid?: string; // 存储具体选择的UUID
  isDefault?: boolean;
  isPublic?: boolean;
  memberAssignments?: TaskBoardMemberAssignment[];
  sortWeight?: number;
  tenantId?: string;
  organizationId?: string;
}

// 查询任务看板DTO
export interface QueryTaskBoardDto {
  page?: number;
  limit?: number;
  keyword?: string;
  type?: string;
  isDefault?: boolean;
  isPublic?: boolean;
  creatorMemberUuid?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'sortWeight' | 'name';
  sortOrder?: 'ASC' | 'DESC';
}

// 看板任务统计信息
export interface TaskBoardStats {
  total: number;
  todo: number;
  inProgress: number;
  blocked: number;
  completed: number;
  review: number;
}

// 带任务数据的看板
export interface TaskBoardWithTasks {
  id: number;
  uuid: string;
  name: string;
  description: string;
  type: 'personal' | 'member' | 'list' | 'tag';
  typeUuid?: string;
  isDefault: boolean;
  isPublic: boolean;
  memberAssignments: TaskBoardMemberAssignment[];
  currentUserRole: TaskBoardCurrentUserRole;
  sortWeight: number;
  creatorMemberUuid: string;
  tenantId: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  stats: TaskBoardStats;
  recentTasks: Task[];
  parentUuid?: string;
}

// 更新标签DTO
export interface UpdateTagDto {
  name?: string;
  color?: string;
  description?: string;
  icon?: string;
  isPublic?: boolean;
  sharedMemberUuids?: string[];
}

// 查询标签DTO
export interface QueryTagDto {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'usageCount' | 'createdAt';
  order?: 'ASC' | 'DESC';
  isPublic?: boolean;
  creatorMemberId?: string;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
}

export interface UserInfo {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  username: string;
  email: string;
  phone: string;
  realName?: string | null;
  nickname?: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'suspended' | 'locked';
  type: 'admin' | 'user' | 'guest';
  lastLoginAt?: string;
  lastLoginIp?: string;
  lastLoginOrganizationUuid?: string;
  passwordHash?: string | null;
  passwordSalt?: string | null;
  passwordExpiresAt?: string | null;
  accountExpiresAt?: string | null;
  loginFailCount: number;
  lockedAt?: string | null;
  settings: Record<string, any>;
  remark?: string;
}

export interface OrganizationInfo {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  name: string;
  description?: string;
  code: string;
  type: 'department' | 'team' | 'company' | 'branch';
  status: 'active' | 'inactive' | 'archived';
  tenantUuid: string;
  parentUuid?: string;
  level: number;
  sortOrder: number;
  settings?: Record<string, unknown>;
  managerUuid?: string;
}

export interface OrganizationMember {
  // 实际数据结构：MemberProfile 是根对象，包含 user 和 organization
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  tenantUuid: string;
  organizationUuid: string;
  memberUuid: string;
  displayName?: string;
  avatarUrl?: string;
  nickname?: string;
  bio?: string;
  tags: string[];
  showRealName: boolean;
  showAvatar: boolean;
  preferences: Record<string, unknown>;
  remark?: string;
  user: UserInfo;
  organization: OrganizationInfo;
  position?: string;
  email?: string;
  member: {
    uuid: string;
    name?: string;
    avatar?: string;
    position?: string;
    email?: string;
  };
  // 兼容性字段（保持向后兼容）
  isEnabled: boolean;
  isAdmin: boolean;
  joinedAt: string;
}

export interface MemberProfile {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  tenantUuid: string;
  organizationUuid: string;
  memberUuid: string;
  displayName?: string;
  avatarUrl?: string;
  nickname?: string;
  bio?: string;
  tags: string[];
  showRealName: boolean;
  showAvatar: boolean;
  preferences: Record<string, unknown>;
  remark?: string;
  user: UserInfo;
  organization: OrganizationInfo;
}

export interface TaskAttachment {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
  filePath?: string;
  taskId: string;
  uploaderMemberId: string;
  name: string;
  originalName: string;
  uploader?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
}

export type UploadProvider = 's3' | 'oss' | 'cos';

export interface UploadConfig {
  activeProvider: UploadProvider;
  maxFileSizeMB: number;
  allowedMimeTypes: string[];
  providers: {
    s3: {
      endpoint?: string;
      region: string;
      bucket: string;
      accessKeyId: string;
      secretAccessKey: string;
      sessionToken?: string;
      forcePathStyle: boolean;
      publicBaseUrl?: string;
      keyPrefix?: string;
    };
    oss: {
      endpoint: string;
      region?: string;
      bucket: string;
      accessKeyId: string;
      accessKeySecret: string;
      stsToken?: string;
      publicBaseUrl?: string;
      keyPrefix?: string;
    };
    cos: {
      region: string;
      bucket: string;
      secretId: string;
      secretKey: string;
      sessionToken?: string;
      publicBaseUrl?: string;
      keyPrefix?: string;
    };
  };
}

export interface TaskList {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  name: string;
  description?: string;
  type: TaskListType;
  queryConditions?: Record<string, any>;
  color?: string;
  icon?: string;
  isDefault: boolean;
  isPublic: boolean;
  sortWeight: number;
  creatorMemberUuid: string;
  sharedMemberUuids?: string[];
  tenantId: string;
  organizationId: string;
  creator?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
  taskRelations?: Array<{
    uuid: string;
    sortWeight: number;
    groupName?: string;
    task: Task;
  }>;
}

export interface TaskActivity {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  occurredAt?: string;
  isDeleted: boolean;
  taskId: string;
  activityType: string;
  description: string;
  details?: Record<string, any>;
  actorMemberId: string;
  tenantId: string;
  organizationId: string;
  actorMember?: {
    uuid: string;
    name: string;
    displayName?: string;
    avatar?: string;
    avatarUrl?: string;
  };
  task?: {
    uuid: string;
    title: string;
  };
}

export interface CreateTaskDto {
  title: string;
  content?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  startTime?: string;
  dueDate?: string;
  assignerMemberUuid?: string;
  parentTaskId?: string;
  tags?: string[];
  color?: string;
  progress?: number;
  tenantId?: string;
  organizationId?: string;
  watcherMemberIds?: string[];
  taskListIds?: string[];
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
  taskMembers?: Array<{
    memberUuid: string;
    roleCode?: string;
    roleName?: string;
    isPrimary?: boolean;
  }>;
}

export interface UpdateTaskDto {
  title?: string;
  content?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  startTime?: string;
  dueDate?: string;
  parentTaskId?: string;
  tags?: string[];
  color?: string;
  progress?: number;
  watcherMemberIds?: string[];
  taskListIds?: string[];
  taskMembers?: Array<{
    memberUuid: string;
    roleCode?: string;
    roleName?: string;
    isPrimary?: boolean;
  }>;
}

export interface CreateTaskListDto {
  name: string;
  description?: string;
  type?: TaskListType;
  queryConditions?: Record<string, any>;
  color?: string;
  icon?: string;
  isDefault?: boolean;
  isPublic?: boolean;
  sharedMemberUuids?: string[];
  sortWeight?: number;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
}

export interface UpdateTaskListDto {
  name?: string;
  description?: string;
  type?: TaskListType;
  queryConditions?: Record<string, any>;
  color?: string;
  icon?: string;
  isDefault?: boolean;
  isPublic?: boolean;
  sharedMemberUuids?: string[];
  sortWeight?: number;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
}

export interface TaskStatistics {
  total: number;
  completed: number;
  inProgress: number;
  overdue: number;
  startToday: number;
  startThisWeek: number;
  startCurrentWeek: number;
  byStatus: {
    todo: number;
    in_progress: number;
    blocked?: number;
    review: number;
  };
  byPriority: {
    none: number;
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
}

export interface TaskNavPendingCounts {
  assignee: number;
  today: number;
  week: number;
  next7Days: number;
  allTask: number;
  create: number;
  assigner: number;
  watched: number;
}

export interface DashboardStatistics {
  summary: {
    total: number;
    inProgress: number;
    completed: number;
    overdue: number;
    completionRate: number;
  };
  risk: {
    overdueCount: number;
    dueSoon48hCount: number;
    riskRate: number;
  };
  distribution: {
    byStatus: {
      todo: number;
      in_progress: number;
      blocked?: number;
      review: number;
    };
    byPriority: {
      none: number;
      low: number;
      medium: number;
      high: number;
      urgent: number;
    };
  };
  trends: {
    weekly: Array<{
      date: string;
      created: number;
      completed: number;
    }>;
    monthly: Array<{
      date: string;
      created: number;
      completed: number;
    }>;
  };
}

// 重新定义标签相关类型，避免循环导入问题
export interface Tag {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  name: string;
  color?: string;
  description?: string;
  icon?: string;
  usageCount?: number;
  taskCount?: number;
  tenantId: string;
  organizationId: string;
  creatorMemberUuid?: string;
  creatorMemberId?: string;
  isPublic?: boolean;
  sharedMemberUuids?: string[];
  creator?: {
    uuid: string;
    name: string;
    avatar?: string;
  };
}

export interface CreateTagDto {
  name: string;
  color?: string;
  description?: string;
  icon?: string;
  isPublic?: boolean;
  sharedMemberUuids?: string[];
  tenantId?: string;
  organizationId?: string;
  sourceAppUuid?: string;
  sourceSceneUuid?: string;
  sourceObjectUuid?: string;
}

// 日报数据结构
export interface DailyReportData {
  id?: string;
  uuid?: string;
  date: string;
  userId: string;
  // 自动抓取的任务数据
  taskStats: {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    pendingTasks: number;
    completionRate: number;
  };
  tasks: Task[];
  // 手动补充的内容
  taskProgress: TaskProgressItem[];
  summary: string;
  problems: string;
  solutions: string;
  reflection: string;
  createdAt?: string;
  updatedAt?: string;
}

// 任务进展项
export interface TaskProgressItem {
  taskId: string;
  taskTitle: string;
  progress: string;
  status: TaskStatus;
}

// 创建日报DTO
export interface CreateDailyReportDto {
  date: string;
  taskProgress: TaskProgressItem[];
  summary: string;
  problems: string;
  solutions: string;
  reflection: string;
}

// 更新日报DTO
export interface UpdateDailyReportDto {
  taskProgress?: TaskProgressItem[];
  summary?: string;
  problems?: string;
  solutions?: string;
  reflection?: string;
}

// 日报查询DTO
export interface QueryDailyReportDto {
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

// 日报统计数据
export interface DailyReportStatistics {
  totalReports: number;
  avgCompletionRate: number;
  totalTasksCompleted: number;
  mostProductiveDay: string;
  reportsByMonth: Record<string, number>;
}

// 看板面板实体
export interface TaskBoardPanelEntity {
  id: number;
  uuid: string;
  panelId: string;
  title: string;
  description?: string;
  color?: string;
  icon?: string;
  type: string;
  typeUuid?: string;
  parentUuid?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  sortWeight: number;
  isVisible: boolean;
  taskBoardUuid: string;
  taskListUuid?: string; // 关联的任务清单UUID
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

// 创建看板面板DTO
export interface CreateTaskBoardPanelDto {
  panelId: string;
  title: string;
  description?: string;
  color?: string;
  icon?: string;
  type?: string;
  typeUuid?: string;
  parentUuid?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  sortWeight?: number;
  isVisible?: boolean;
}

// 更新看板面板DTO
export interface UpdateTaskBoardPanelDto {
  panelId?: string;
  title?: string;
  description?: string;
  color?: string;
  icon?: string;
  type?: string;
  typeUuid?: string;
  parentUuid?: string;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  sortWeight?: number;
  isVisible?: boolean;
}

export interface TaskCompletionUndo {
  restoreProgress: number;
  expectedCompletionToken: string;
}
